import React, { createContext, useContext, useState, useEffect } from 'react';
import { ApiClient } from '../api/client';
import type { SystemBrandingConfig } from '../types';
import { Shield, ShieldCheck, ShieldAlert } from 'lucide-react';

const DEFAULT_BRANDING: SystemBrandingConfig = {
  title_prefix: 'Közzé',
  title_middle: 'Tér',
  title_suffix: 'Pro.',
  system_title: 'KözzéTér Pro.',
  subtitle: '2011. évi CXII. törvény szerinti közzétételi platform',
  version: 'v4.5',
  shield_type: 'shield',
  shield_color: 'green',
  logo_url: '',
  prefix_color: '#16a34a',
  middle_color: '#0f172a',
  suffix_color: '#16a34a',
  brand_color: '#16a34a',
  brand_color2: '#059669',
  public_portal_timeout: 30,
  totp_issuer: 'KözzéTér Pro',
};

interface SystemBrandingContextType {
  branding: SystemBrandingConfig;
  loading: boolean;
  updateBranding: (data: Partial<SystemBrandingConfig>) => Promise<{ ok: boolean; error?: string }>;
  reloadBranding: () => Promise<void>;
}

const SystemBrandingContext = createContext<SystemBrandingContextType | undefined>(undefined);

export const SystemBrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<SystemBrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const reloadBranding = async () => {
    try {
      const res = await ApiClient.getSystemBranding();
      if (res.ok && res.data) {
        setBranding({ ...DEFAULT_BRANDING, ...res.data });
      }
    } catch (e) {
      console.warn('Nem sikerült betölteni a system brandinget:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadBranding();
  }, []);

  const updateBranding = async (data: Partial<SystemBrandingConfig>) => {
    try {
      const res = await ApiClient.updateSystemBranding(data);
      if (res.ok && res.data) {
        setBranding({ ...DEFAULT_BRANDING, ...res.data });
        return { ok: true };
      }
      return { ok: false, error: res.error || 'Mentési hiba' };
    } catch (err: any) {
      return { ok: false, error: err.message || 'Hálózati hiba' };
    }
  };

  return (
    <SystemBrandingContext.Provider value={{ branding, loading, updateBranding, reloadBranding }}>
      {children}
    </SystemBrandingContext.Provider>
  );
};

export const useSystemBranding = (): SystemBrandingContextType => {
  const ctx = useContext(SystemBrandingContext);
  if (!ctx) {
    return {
      branding: DEFAULT_BRANDING,
      loading: false,
      updateBranding: async () => ({ ok: false }),
      reloadBranding: async () => {},
    };
  }
  return ctx;
};

// ── SEGÉDKOMPONENSEK: LOGÓ ÉS CÍM MEGJELENÍTŐK ────────────────────

export interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  overrideConfig?: Partial<SystemBrandingConfig>;
}

export const ShieldLogo: React.FC<LogoProps> = ({ size = 'md', className = '', overrideConfig }) => {
  const { branding } = useSystemBranding();
  const cfg = { ...branding, ...overrideConfig };

  const sizeClasses = {
    sm: 'w-7 h-7 rounded-lg text-sm',
    md: 'w-8 h-8 rounded-lg text-base',
    lg: 'w-12 h-12 rounded-xl text-xl',
    xl: 'w-16 h-16 rounded-2xl text-2xl',
  }[size];

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4.5 h-4.5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8',
  }[size];

  // Színgradiens témák
  const colorGradients: Record<string, string> = {
    green: 'from-emerald-600 to-green-500 text-white shadow-emerald-500/20 ring-emerald-500/10',
    indigo: 'from-indigo-600 to-indigo-500 text-white shadow-indigo-500/20 ring-indigo-500/10',
    blue: 'from-blue-600 to-indigo-600 text-white shadow-blue-500/20 ring-blue-500/10',
    slate: 'from-slate-800 to-slate-700 text-white shadow-slate-500/20 ring-slate-500/10',
  };

  const gradient = colorGradients[cfg.shield_color] || colorGradients.green;

  if (cfg.shield_type === 'custom' && cfg.logo_url) {
    return (
      <div className={`relative inline-flex items-center justify-center overflow-hidden shrink-0 ${sizeClasses} ${className}`}>
        <img src={cfg.logo_url} alt={cfg.system_title} className="w-full h-full object-contain" />
      </div>
    );
  }

  const renderIcon = () => {
    switch (cfg.shield_type) {
      case 'shield_check':
        return <ShieldCheck className={iconSizes} />;
      case 'shield_alert':
        return <ShieldAlert className={iconSizes} />;
      default:
        return <Shield className={iconSizes} />;
    }
  };

  return (
    <div
      className={`inline-flex items-center justify-center bg-gradient-to-tr ${gradient} shadow-md ring-4 shrink-0 transition-transform ${sizeClasses} ${className}`}
    >
      {renderIcon()}
    </div>
  );
};

export interface TitleProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  overrideConfig?: Partial<SystemBrandingConfig>;
}

export const BrandTitle: React.FC<TitleProps> = ({ size = 'md', className = '', overrideConfig }) => {
  const { branding } = useSystemBranding();
  const cfg = { ...branding, ...overrideConfig };

  const sizeClasses = {
    sm: 'text-sm font-bold',
    md: 'text-base font-bold',
    lg: 'text-2xl font-extrabold',
    xl: 'text-3xl sm:text-4xl font-extrabold',
  }[size];

  // Háromtagú megjelenés: ZÖLD (pl. "Közzé") + FEKETE/FEHÉR (pl. "Tér") + ZÖLD (pl. "Pro.")
  // Visszafelé kompatibilis a korábbi 2-tagú "Köz" (fekete) + "adat" (zöld) beállítással is
  const prefix = cfg.title_prefix ?? 'Közzé';
  const middle = cfg.title_middle !== undefined ? cfg.title_middle : (prefix === 'Köz' ? '' : 'Tér');
  const suffix = cfg.title_suffix ?? (prefix === 'Köz' ? 'adat' : 'Pro.');

  const isLegacyKozadat = prefix === 'Köz' && !middle;

  if (isLegacyKozadat) {
    return (
      <span className={`tracking-tight font-sans select-none inline-flex items-baseline ${sizeClasses} ${className}`}>
        <span className="text-slate-900 dark:text-white transition-colors">
          {prefix}
        </span>
        <span className="text-emerald-600 dark:text-emerald-400 font-extrabold transition-colors">
          {suffix}
        </span>
      </span>
    );
  }

  return (
    <span className={`tracking-tight font-sans select-none inline-flex items-baseline gap-0.5 ${sizeClasses} ${className}`}>
      {/* 1. Tag (ZÖLD) - pl. Közzé */}
      <span className="text-emerald-600 dark:text-emerald-400 font-extrabold transition-colors">
        {prefix}
      </span>
      {/* 2. Tag (FEKETE/FEHÉR) - pl. Tér */}
      {middle && (
        <span className="text-slate-900 dark:text-white font-bold transition-colors">
          {middle}
        </span>
      )}
      {/* 3. Tag (ZÖLD) - pl. Pro. */}
      {suffix && (
        <span className="text-emerald-600 dark:text-emerald-400 font-extrabold transition-colors ml-0.5">
          {suffix}
        </span>
      )}
    </span>
  );
};
